import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { shiftLogApi, estimatesApi, adminApi } from '../services/api';
import { useEstimates } from '../hooks/useEstimates';
import useAuthStore from '../store/authStore';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { DailyGenerationSummary, GenerationEstimate } from '../types';
import {
  BS_MONTHS_FULL, BS_MONTHS_SHORT,
  adStrToBS, adStrToBSSafe, bsToAD, bsDaysInMonth, bsMonthRangeAD, bsYearRangeAD,
  fmtAD, formatBSDayLabel, formatBSDateFull, getBSYearOptions,
  getCurrentBSYear, getCurrentBSMonth, getCurrentBSDay,
} from '../utils/bs';

type Period    = 'daily' | 'weekly' | 'monthly' | 'yearly';
type GenPeriod = 'hourly' | Period;

const DAY_ABBRS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GEN_PERIODS: GenPeriod[] = ['hourly', 'daily', 'weekly', 'monthly', 'yearly'];
const ERC_PERIODS: GenPeriod[] = ['hourly', 'daily', 'weekly', 'monthly', 'yearly'];

const BS_YEAR_OPTIONS = getBSYearOptions(10);

// MWh-based formatters for charts that pre-divide by 1000
function fmtMwhAxis(mwh: number): string {
  if (mwh === 0) return '0';
  if (mwh >= 1_000) return `${(mwh / 1_000).toFixed(1)} GWh`;
  return `${Math.round(mwh)} MWh`;
}
function fmtMwh(mwh: number): string {
  if (mwh >= 1_000) return `${(mwh / 1_000).toFixed(2)} GWh`;
  return `${mwh.toFixed(2)} MWh`;
}

type EnergyUnit    = 'kWh' | 'MWh' | 'GWh';
type PowerUnit     = 'kW'  | 'MW';
type GenExportType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';

// Source values come in MWh (already ÷1000 from kWh at query time)
function applyEnergyUnit(mwh: number, unit: EnergyUnit): number {
  if (unit === 'kWh') return +(mwh * 1000).toFixed(2);
  if (unit === 'GWh') return +(mwh / 1000).toFixed(4);
  return +mwh.toFixed(2);
}
function applyPowerUnit(kw: number, unit: PowerUnit): number {
  if (unit === 'MW') return +(kw / 1000).toFixed(3);
  return +kw.toFixed(2);
}
function unitPrecision(unit: EnergyUnit | PowerUnit): number {
  if (unit === 'GWh') return 4;
  if (unit === 'MW')  return 3;
  return 2;
}

// ─── ISO week helpers (operate on AD dates, labels in BS) ─────────────────────

function isoWeekNum(d: Date): number {
  const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = u.getUTCDay() || 7;
  u.setUTCDate(u.getUTCDate() + 4 - day);
  const jan1 = new Date(Date.UTC(u.getUTCFullYear(), 0, 1));
  return Math.ceil(((u.getTime() - jan1.getTime()) / 86_400_000 + 1) / 7);
}

function isoWeekYear(d: Date): number {
  const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = u.getUTCDay() || 7;
  u.setUTCDate(u.getUTCDate() + 4 - day);
  return u.getUTCFullYear();
}

function isoWeekMonday(year: number, weekNo: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - jan4Day + 1 + (weekNo - 1) * 7);
  return mon;
}

// Week label using BS day numbers: "W23 (Mon 21–Sun 27)"
function bsWeekLabel(d: Date): string {
  if (isNaN(d.getTime())) return 'Unknown';
  const wk  = isoWeekNum(d);
  const wkY = isoWeekYear(d);
  const monAD = isoWeekMonday(wkY, wk);
  const sunAD = new Date(monAD); sunAD.setDate(monAD.getDate() + 6);
  const monBS = adStrToBS(fmtAD(monAD));
  const sunBS = adStrToBS(fmtAD(sunAD));
  return `W${wk} (${DAY_ABBRS[monAD.getDay()]} ${monBS.getDate()}–${DAY_ABBRS[sunAD.getDay()]} ${sunBS.getDate()})`;
}

// Enumerate ISO weeks that fall within a BS year (for drill-down)
function weeksInBSYear(bsYear: number): { weekNo: number; label: string; from: string; to: string }[] {
  const { from: bsYearFrom, to: bsYearTo } = bsYearRangeAD(bsYear);
  const result: { weekNo: number; label: string; from: string; to: string }[] = [];
  const d = new Date(bsYearFrom + 'T00:00:00');
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1); // advance to first Monday

  while (true) {
    const start    = new Date(d);
    const endFull  = new Date(d); endFull.setDate(endFull.getDate() + 6);
    const fromStr  = fmtAD(start);
    if (fromStr > bsYearTo) break;
    const toStrFull = fmtAD(endFull);
    const toStr     = toStrFull > bsYearTo ? bsYearTo : toStrFull;
    const endDate   = new Date(toStr + 'T00:00:00');
    const wk        = isoWeekNum(start);
    const startBS   = adStrToBS(fromStr);
    const endBS     = adStrToBS(toStr);
    result.push({
      weekNo: wk,
      label:  `W${wk}: ${DAY_ABBRS[start.getDay()]} ${startBS.getDate()} ${BS_MONTHS_SHORT[startBS.getMonth()]} – ${DAY_ABBRS[endDate.getDay()]} ${endBS.getDate()} ${BS_MONTHS_SHORT[endBS.getMonth()]}`,
      from:   fromStr,
      to:     toStr,
    });
    d.setDate(d.getDate() + 7);
  }
  return result;
}

// ─── Date range from BS selectors ────────────────────────────────────────────

function getRange(period: Period, bsYear: number, bsMonthIdx: number, _bsDay: number) {
  if (period === 'daily')   return bsMonthRangeAD(bsYear, bsMonthIdx); // full month, one bar per day
  if (period === 'weekly')  return bsMonthRangeAD(bsYear, bsMonthIdx);
  if (period === 'monthly') return bsYearRangeAD(bsYear);
  // yearly: last 5 BS years
  return { from: bsYearRangeAD(bsYear - 4).from, to: bsYearRangeAD(bsYear).to };
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function downloadCsv(rows: Record<string, string | number>[], filename: string): void {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => String(r[h] ?? '')).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Aggregation ──────────────────────────────────────────────────────────────

type GenRow = { label: string; actual: number; planned?: number | null };
type ErcRow = { label: string; planned: number; actual: number };

function aggregateGen(summaries: DailyGenerationSummary[], period: Period, bsYear: number): GenRow[] {
  if (period === 'daily') {
    return summaries.map(s => {
      const adDate = new Date(s.date + 'T00:00:00');
      return { label: formatBSDayLabel(adDate), actual: +(Number(s.total_generation_kwh) / 1000).toFixed(2) };
    });
  }
  if (period === 'weekly') {
    const map: Record<string, { label: string; actual: number; sort: string }> = {};
    summaries.forEach(s => {
      const d = new Date(s.date + 'T00:00:00');
      if (isNaN(d.getTime())) return;
      const wk = isoWeekNum(d); const wkY = isoWeekYear(d);
      const key = `${wkY}-W${String(wk).padStart(2, '0')}`;
      if (!map[key]) map[key] = { label: bsWeekLabel(d), actual: 0, sort: key };
      map[key].actual += Number(s.total_generation_kwh) / 1000;
    });
    return Object.values(map).sort((a, b) => a.sort > b.sort ? 1 : -1)
      .map(v => ({ label: v.label, actual: +v.actual.toFixed(2) }));
  }
  if (period === 'monthly') {
    // All 12 months pre-seeded with 0 so empty months still show
    const map = new Map<number, GenRow>();
    for (let m = 0; m < 12; m++) map.set(m, { label: BS_MONTHS_SHORT[m], actual: 0 });
    summaries.forEach(s => {
      const nd = adStrToBSSafe(s.date); if (!nd) return;
      if (nd.getYear() !== bsYear) return;
      map.get(nd.getMonth())!.actual += Number(s.total_generation_kwh) / 1000;
    });
    return [...map.entries()].sort(([a], [b]) => a - b)
      .map(([, v]) => ({ ...v, actual: +v.actual.toFixed(2) }));
  }
  // yearly: group by BS year
  const map = new Map<number, GenRow>();
  summaries.forEach(s => {
    const nd = adStrToBSSafe(s.date); if (!nd) return;
    const y = nd.getYear();
    if (!map.has(y)) map.set(y, { label: String(y), actual: 0 });
    map.get(y)!.actual += Number(s.total_generation_kwh) / 1000;
  });
  return [...map.entries()].sort(([a], [b]) => a - b)
    .map(([, v]) => ({ ...v, actual: +v.actual.toFixed(2) }));
}

function buildErcRows(summaries: DailyGenerationSummary[], estimates: GenerationEstimate[], period: Period, bsYear: number): ErcRow[] {
  const ptMap: Record<Period, GenerationEstimate['period_type']> = {
    daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year',
  };
  const est = estimates.filter(e => e.period_type === ptMap[period]);

  if (period === 'daily') {
    const planMap: Record<string, number> = {};
    est.forEach(e => { if (e.date) planMap[e.date] = Number(e.estimated_kwh) / 1000; });
    return summaries.map(s => {
      const adDate = new Date(s.date + 'T00:00:00');
      return {
        label:   formatBSDayLabel(adDate),
        planned: +(planMap[s.date] ?? 0).toFixed(2),
        actual:  +(Number(s.total_generation_kwh) / 1000).toFixed(2),
      };
    });
  }

  if (period === 'weekly') {
    const planMap: Record<string, number> = {};
    est.forEach(e => {
      if (e.week_no != null) { const k = `${e.year}-W${String(e.week_no).padStart(2, '0')}`; planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; }
    });
    const actualMap: Record<string, { label: string; actual: number; sort: string }> = {};
    summaries.forEach(s => {
      const d = new Date(s.date + 'T00:00:00');
      if (isNaN(d.getTime())) return;
      const nd = adStrToBSSafe(s.date); if (!nd) return;
      const wk = isoWeekNum(d);
      // Use BS year to match the key format used when saving estimates (e.year is BS year)
      const k = `${nd.getYear()}-W${String(wk).padStart(2, '0')}`;
      if (!actualMap[k]) actualMap[k] = { label: bsWeekLabel(d), actual: 0, sort: k };
      actualMap[k].actual += Number(s.total_generation_kwh) / 1000;
    });
    return Object.entries(actualMap).sort(([, a], [, b]) => a.sort > b.sort ? 1 : -1).map(([k, v]) => ({
      label: v.label, planned: +(planMap[k] ?? 0).toFixed(2), actual: +v.actual.toFixed(2),
    }));
  }

  if (period === 'monthly') {
    const planMap: Record<string, number> = {};
    est.forEach(e => {
      if (e.month != null) { const k = `${e.year}-${e.month}`; planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; }
    });
    // All 12 months pre-seeded so empty months still show
    const actualMap = new Map<number, ErcRow>();
    for (let m = 0; m < 12; m++) {
      actualMap.set(m, { label: BS_MONTHS_SHORT[m], planned: planMap[`${bsYear}-${m + 1}`] ?? 0, actual: 0 });
    }
    summaries.forEach(s => {
      const nd = adStrToBSSafe(s.date); if (!nd) return;
      if (nd.getYear() !== bsYear) return;
      actualMap.get(nd.getMonth())!.actual += Number(s.total_generation_kwh) / 1000;
    });
    return [...actualMap.entries()].sort(([a], [b]) => a - b)
      .map(([, v]) => ({ ...v, actual: +v.actual.toFixed(2), planned: +v.planned.toFixed(2) }));
  }

  // yearly — single selected BS year
  const planMap: Record<string, number> = {};
  est.forEach(e => { const k = String(e.year); planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000; });
  const actualMap = new Map<number, ErcRow>();
  // Pre-seed the selected year so it shows even when there is no actual data yet
  actualMap.set(bsYear, { label: String(bsYear), planned: planMap[String(bsYear)] ?? 0, actual: 0 });
  summaries.forEach(s => {
    const nd = adStrToBSSafe(s.date); if (!nd) return;
    if (nd.getYear() === bsYear) actualMap.get(bsYear)!.actual += Number(s.total_generation_kwh) / 1000;
  });
  return [...actualMap.entries()].sort(([a], [b]) => a - b)
    .map(([, v]) => ({ ...v, actual: +v.actual.toFixed(2), planned: +v.planned.toFixed(2) }));
}

// ─── Period Controls (BS-aware) ───────────────────────────────────────────────

interface PeriodControlsProps {
  periods:    readonly string[];
  period:     string;   onPeriod:   (p: any) => void;
  bsYear:     number;   onBSYear:   (y: number) => void;
  bsMonth:    number;   onBSMonth:  (m: number) => void; // 0-indexed
  bsDay:      number;   onBSDay:    (d: number) => void;
  selWeek:    number | null; onWeek: (w: number | null) => void;
}

function PeriodControls({
  periods, period, onPeriod, bsYear, onBSYear, bsMonth, onBSMonth, bsDay, onBSDay, selWeek, onWeek,
}: PeriodControlsProps) {
  const weeks    = useMemo(() => period === 'monthly' ? weeksInBSYear(bsYear) : [], [period, bsYear]);
  const daysInMo = useMemo(() => bsDaysInMonth(bsYear, bsMonth), [bsYear, bsMonth]);

  return (
    <div className="flex flex-wrap gap-3 items-end">
      {/* Period buttons */}
      <div className="flex gap-1.5">
        {periods.map(p => (
          <button key={p} onClick={() => { onPeriod(p); onWeek(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              period === p ? 'bg-brand-600 text-white border-brand-600' : 'text-slate-600 border-hydro-border hover:border-slate-400 hover:bg-slate-50'
            }`}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>

      {/* Hourly → BS year / month / day dropdowns */}
      {period === 'hourly' && (
        <>
          <div>
            <label className="block text-xs text-slate-500 mb-1">BS Year</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-sm">
              {BS_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Month</label>
            <select value={bsMonth} onChange={e => { onBSMonth(Number(e.target.value)); onBSDay(1); }} className="input text-sm">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Day</label>
            <select value={bsDay} onChange={e => onBSDay(Number(e.target.value))} className="input text-sm">
              {Array.from({ length: daysInMo }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Daily → BS year / month (full month shown) */}
      {period === 'daily' && (
        <>
          <div>
            <label className="block text-xs text-slate-500 mb-1">BS Year</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-sm">
              {BS_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Month</label>
            <select value={bsMonth} onChange={e => onBSMonth(Number(e.target.value))} className="input text-sm">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Weekly → BS month + BS year */}
      {period === 'weekly' && (
        <>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Month (BS)</label>
            <select value={bsMonth} onChange={e => onBSMonth(Number(e.target.value))} className="input text-sm">
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Year (BS)</label>
            <select value={bsYear} onChange={e => onBSYear(Number(e.target.value))} className="input text-sm">
              {BS_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Monthly → BS year + week drill-down */}
      {period === 'monthly' && (
        <>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Year (BS)</label>
            <select value={bsYear} onChange={e => { onBSYear(Number(e.target.value)); onWeek(null); }} className="input text-sm">
              {BS_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Drill into Week</label>
            <select value={selWeek ?? ''} onChange={e => onWeek(e.target.value ? Number(e.target.value) : null)} className="input text-sm" style={{ minWidth: 300 }}>
              <option value="">— All months —</option>
              {weeks.map(w => <option key={w.weekNo} value={w.weekNo}>{w.label}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared chart components ──────────────────────────────────────────────────

function GenChart({ data, loading, title, unit = 'MWh' }: { data: GenRow[]; loading: boolean; title: string; unit?: string }) {
  const isMwh = unit === 'MWh';
  const hasPlanned = data.some(d => d.planned != null);
  const tickFmt = isMwh
    ? (v: number) => fmtMwhAxis(v)
    : (v: number) => `${v} ${unit}`;
  const tipFmt = isMwh
    ? (v: number, name: string) => [fmtMwh(v), name] as [string, string]
    : (v: number, name: string) => [`${v} ${unit}`, name] as [string, string];

  return (
    <div className="card">
      <p className="text-sm font-semibold text-slate-700 mb-4">{title}</p>
      {loading ? (
        <div className="h-72 flex items-center justify-center text-slate-400">Loading…</div>
      ) : data.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-slate-400">No data for selected period</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={tickFmt} width={72} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
              labelStyle={{ color: '#64748b', fontSize: 11 }} itemStyle={{ fontSize: 11 }}
              formatter={tipFmt}
            />
            {hasPlanned && <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />}
            {hasPlanned && <Bar dataKey="planned" name="Planned" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={hasPlanned ? 40 : 48} />}
            <Bar dataKey="actual" name="Actual" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={hasPlanned ? 40 : 48} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function ErcChart({ data, loading, title, unit = 'MWh' }: { data: ErcRow[]; loading: boolean; title: string; unit?: 'MWh' | 'kW' }) {
  const isMwh = unit === 'MWh';
  const tickFmt = isMwh ? fmtMwhAxis : (v: number) => `${v} kW`;
  const tipFmt  = isMwh
    ? (v: number, name: string) => [fmtMwh(v), name] as [string, string]
    : (v: number, name: string) => [`${v} kW`, name] as [string, string];

  return (
    <div className="card">
      <p className="text-sm font-semibold text-slate-700 mb-4">{title}</p>
      {loading ? (
        <div className="h-72 flex items-center justify-center text-slate-400">Loading…</div>
      ) : data.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-slate-400">No data for selected period</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={tickFmt} width={72} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
              labelStyle={{ color: '#64748b', fontSize: 11 }} itemStyle={{ fontSize: 11 }}
              formatter={tipFmt}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
            <Bar dataKey="planned" name="Planned" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="actual"  name="Actual"  fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Generation Export ────────────────────────────────────────────────────────

function generateGenExcel(
  rows: { label: string; actual: number }[],
  meta: { type: GenExportType; periodLabel: string; plant: string; unit: EnergyUnit | PowerUnit; isHourly: boolean },
) {
  const dp    = unitPrecision(meta.unit);
  const total = rows.reduce((s, r) => s + r.actual, 0);

  const typeLabel: Record<GenExportType, string> = {
    hourly: 'Hourly View', daily: 'Daily View', weekly: 'Weekly Breakdown',
    monthly: 'Monthly Breakdown', yearly: 'Annual Summary',
  };

  const sheetData: (string | number)[][] = [
    ['GENERATION REPORT', '', ''],
    [''],
    ['Plant:', meta.plant, ''],
    ['Period:', meta.periodLabel, ''],
    ['Report Type:', typeLabel[meta.type], ''],
    ['Generated:', formatBSDateFull(new Date()), ''],
    ['Unit:', meta.unit, ''],
    [''],
    ['S.No.', meta.isHourly ? 'Hour' : 'Period (BS)', `Actual Generation (${meta.unit})`],
    ...rows.map((r, i) => [i + 1, r.label, +r.actual.toFixed(dp)]),
    ...(meta.isHourly ? [] : [
      [''],
      ['', 'TOTAL', +total.toFixed(dp)],
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  ws['!cols'] = [{ wch: 6 }, { wch: 32 }, { wch: 26 }];
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Generation');
  const safePeriod = meta.periodLabel.replace(/[^a-zA-Z0-9_-]/g, '_');
  XLSX.writeFile(wb, `Generation_${meta.type}_${safePeriod}.xlsx`);
}

function GenExportModal({ plantId, plantName, onClose, initPeriod, initYear, initMonth, initDay, initWeek }: {
  plantId: number;
  plantName: string;
  onClose: () => void;
  initPeriod: GenPeriod;
  initYear: number;
  initMonth: number;
  initDay: number;
  initWeek: number | null;
}) {
  const curYear  = getCurrentBSYear();
  const curMonth = getCurrentBSMonth();
  const curDay   = getCurrentBSDay();

  const [expType,   setExpType]   = useState<GenExportType>(initWeek != null ? 'weekly' : initPeriod);
  const [year,      setYear]      = useState(initYear);
  const [month,     setMonth]     = useState(initMonth);
  const [day,       setDay]       = useState(initDay);
  // daily range — default to the currently selected month
  const [fromYear,  setFromYear]  = useState(initYear);
  const [fromMonth, setFromMonth] = useState(initMonth);
  const [fromDay,   setFromDay]   = useState(1);
  const [toYear,    setToYear]    = useState(initYear);
  const [toMonth,   setToMonth]   = useState(initMonth);
  const [toDay,     setToDay]     = useState(bsDaysInMonth(initYear, initMonth));

  const [eUnit,   setEUnit]   = useState<EnergyUnit>('MWh');
  const [pUnit,   setPUnit]   = useState<PowerUnit>('kW');
  const [loading, setLoading] = useState(false);

  const yearOptions = getBSYearOptions(10);
  const isHourly    = expType === 'hourly';
  const isDaily     = expType === 'daily';
  const isYearly    = expType === 'yearly';
  const needsYear   = !isYearly;
  const needsMonth  = expType === 'hourly' || expType === 'weekly';

  const fromDaysInMo = bsDaysInMonth(fromYear, fromMonth);
  const toDaysInMo   = bsDaysInMonth(toYear, toMonth);
  const dayInMo      = bsDaysInMonth(year, month);

  const YEARLY_COUNT = 5;
  const yearlyRange  = Array.from({ length: YEARLY_COUNT }, (_, i) => curYear - YEARLY_COUNT + 1 + i);

  async function handleExport() {
    setLoading(true);
    try {
      let rows: { label: string; actual: number }[];
      let periodLabel: string;
      const unit: EnergyUnit | PowerUnit = isHourly ? pUnit : eUnit;

      if (expType === 'hourly') {
        const date = fmtAD(bsToAD(year, month, day));
        const res  = await shiftLogApi.hourlyTotals({ date });
        rows = (res.data.data as Array<{ hour: number; combined_kw: string }>).map(h => ({
          label:  String(h.hour).padStart(2, '0') + ':00',
          actual: applyPowerUnit(Number(h.combined_kw), pUnit),
        }));
        const nd = adStrToBSSafe(date);
        periodLabel = nd ? `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}` : date;

      } else if (expType === 'daily') {
        const from = fmtAD(bsToAD(fromYear, fromMonth, fromDay));
        const to   = fmtAD(bsToAD(toYear,   toMonth,   toDay));
        const res  = await shiftLogApi.dailyTotals({ from, to });
        const sums: DailyGenerationSummary[] = res.data.data;
        rows = sums.map(s => ({
          label:  formatBSDayLabel(new Date(s.date + 'T00:00:00')),
          actual: applyEnergyUnit(Number(s.total_generation_kwh) / 1000, eUnit),
        }));
        const fBS = adStrToBSSafe(from); const tBS = adStrToBSSafe(to);
        periodLabel = fBS && tBS
          ? `${fBS.getDate()} ${BS_MONTHS_SHORT[fBS.getMonth()]} ${fBS.getYear()} – ${tBS.getDate()} ${BS_MONTHS_SHORT[tBS.getMonth()]} ${tBS.getYear()}`
          : `${from} to ${to}`;

      } else if (expType === 'weekly') {
        const { from, to } = bsMonthRangeAD(year, month);
        const res   = await shiftLogApi.dailyTotals({ from, to });
        const sums: DailyGenerationSummary[] = res.data.data;
        const wkMap: Record<string, { label: string; mwh: number; sort: string }> = {};
        sums.forEach(s => {
          const d = new Date(s.date + 'T00:00:00'); if (isNaN(d.getTime())) return;
          const wk = isoWeekNum(d); const wkY = isoWeekYear(d);
          const k  = `${wkY}-W${String(wk).padStart(2, '0')}`;
          if (!wkMap[k]) wkMap[k] = { label: bsWeekLabel(d), mwh: 0, sort: k };
          wkMap[k].mwh += Number(s.total_generation_kwh) / 1000;
        });
        rows = Object.entries(wkMap).sort(([, a], [, b]) => a.sort > b.sort ? 1 : -1)
          .map(([, v]) => ({ label: v.label, actual: applyEnergyUnit(v.mwh, eUnit) }));
        periodLabel = `${BS_MONTHS_FULL[month]} ${year}`;

      } else if (expType === 'monthly') {
        const { from, to } = bsYearRangeAD(year);
        const res   = await shiftLogApi.dailyTotals({ from, to });
        const sums: DailyGenerationSummary[] = res.data.data;
        const moMap = new Map<number, number>();
        for (let m = 0; m < 12; m++) moMap.set(m, 0);
        sums.forEach(s => {
          const nd = adStrToBSSafe(s.date); if (!nd || nd.getYear() !== year) return;
          moMap.set(nd.getMonth(), (moMap.get(nd.getMonth()) ?? 0) + Number(s.total_generation_kwh) / 1000);
        });
        rows = [...moMap.entries()].sort(([a], [b]) => a - b)
          .map(([m, mwh]) => ({ label: BS_MONTHS_SHORT[m], actual: applyEnergyUnit(mwh, eUnit) }));
        periodLabel = `BS Year ${year}`;

      } else {
        // yearly — last N years automatically
        const { from } = bsYearRangeAD(yearlyRange[0]);
        const { to }   = bsYearRangeAD(yearlyRange[yearlyRange.length - 1]);
        const res = await shiftLogApi.dailyTotals({ from, to });
        const sums: DailyGenerationSummary[] = res.data.data;
        const yrMap = new Map<number, number>(yearlyRange.map(y => [y, 0]));
        sums.forEach(s => {
          const nd = adStrToBSSafe(s.date); if (!nd) return;
          const y = nd.getYear();
          if (yrMap.has(y)) yrMap.set(y, yrMap.get(y)! + Number(s.total_generation_kwh) / 1000);
        });
        rows = yearlyRange.map(y => ({ label: String(y), actual: applyEnergyUnit(yrMap.get(y) ?? 0, eUnit) }));
        periodLabel = `${yearlyRange[0]}–${yearlyRange[yearlyRange.length - 1]}`;
      }

      generateGenExcel(rows, { type: expType, periodLabel, plant: plantName, unit, isHourly });
      onClose();
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  const previewText = expType === 'hourly'
    ? `Hourly generation for ${day} ${BS_MONTHS_FULL[month]} ${year} — 24 rows (one per hour)`
    : expType === 'daily'
    ? `Daily totals from ${fromDay} ${BS_MONTHS_FULL[fromMonth]} ${fromYear} to ${toDay} ${BS_MONTHS_FULL[toMonth]} ${toYear}`
    : expType === 'weekly'
    ? `Weekly breakdown for ${BS_MONTHS_FULL[month]} ${year} — one row per week`
    : expType === 'monthly'
    ? `Monthly breakdown for BS Year ${year} — 12 rows`
    : `All years ${yearlyRange[0]}–${yearlyRange[yearlyRange.length - 1]} — one row per year`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-slate-900">Export Generation Data</h2>
            <p className="text-xs text-slate-500 mt-0.5">Download as Excel (.xlsx)</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 transition-colors">✕</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Export type */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Export Type</p>
            <div className="grid grid-cols-5 gap-1.5">
              {(['hourly', 'daily', 'weekly', 'monthly', 'yearly'] as GenExportType[]).map(t => (
                <button key={t} onClick={() => setExpType(t)}
                  className={`py-2 rounded-lg text-xs font-medium capitalize border transition-colors ${
                    expType === t ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
                  }`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Hourly: year + month + day */}
          {expType === 'hourly' && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">BS Year</label>
                  <select value={year} onChange={e => setYear(Number(e.target.value))} className="input w-full">
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Month</label>
                  <select value={month} onChange={e => { setMonth(Number(e.target.value)); setDay(1); }} className="input w-full">
                    {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Day</label>
                  <select value={day} onChange={e => setDay(Number(e.target.value))} className="input w-full">
                    {Array.from({ length: dayInMo }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Daily: from + to BS date range */}
          {expType === 'daily' && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date Range</p>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">From</label>
                <div className="grid grid-cols-3 gap-2">
                  <select value={fromYear} onChange={e => setFromYear(Number(e.target.value))} className="input">
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={fromMonth} onChange={e => { setFromMonth(Number(e.target.value)); setFromDay(1); }} className="input">
                    {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                  <select value={fromDay} onChange={e => setFromDay(Number(e.target.value))} className="input">
                    {Array.from({ length: fromDaysInMo }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">To</label>
                <div className="grid grid-cols-3 gap-2">
                  <select value={toYear} onChange={e => setToYear(Number(e.target.value))} className="input">
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={toMonth} onChange={e => { setToMonth(Number(e.target.value)); setToDay(1); }} className="input">
                    {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                  <select value={toDay} onChange={e => setToDay(Number(e.target.value))} className="input">
                    {Array.from({ length: toDaysInMo }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Weekly: year + month */}
          {expType === 'weekly' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">BS Year</p>
                <select value={year} onChange={e => setYear(Number(e.target.value))} className="input w-full">
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">BS Month</p>
                <select value={month} onChange={e => setMonth(Number(e.target.value))} className="input w-full">
                  {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Monthly: year only */}
          {expType === 'monthly' && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">BS Year</p>
              <select value={year} onChange={e => setYear(Number(e.target.value))} className="input w-full">
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}

          {/* Unit */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Unit</p>
            {isHourly ? (
              <div className="flex gap-2">
                {(['kW', 'MW'] as PowerUnit[]).map(u => (
                  <button key={u} onClick={() => setPUnit(u)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      pUnit === u ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
                    }`}>
                    {u}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex gap-2">
                {(['kWh', 'MWh', 'GWh'] as EnergyUnit[]).map(u => (
                  <button key={u} onClick={() => setEUnit(u)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      eUnit === u ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
                    }`}>
                    {u}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="bg-slate-50 border border-hydro-border rounded-lg px-4 py-3">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Preview</p>
            <p className="text-sm text-slate-700">{previewText}</p>
            <p className="text-xs text-slate-400 mt-1">Columns: {isHourly ? 'Hour' : 'Period (BS)'} · Actual ({isHourly ? pUnit : eUnit})</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3 justify-end border-t border-hydro-border pt-4">
          <button onClick={onClose} className="btn-ghost text-sm px-4">Cancel</button>
          <button onClick={handleExport} disabled={loading} className="btn-primary text-sm px-5 disabled:opacity-60">
            {loading ? 'Generating…' : '⬇ Export Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Generation Tab ───────────────────────────────────────────────────────────

function GenerationTab() {
  const { user } = useAuthStore();
  const plantId  = user?.plant_id ?? 1;

  const [period,     setPeriod]     = useState<GenPeriod>('monthly');
  const [bsYear,     setBSYear]     = useState(getCurrentBSYear());
  const [bsMonth,    setBSMonth]    = useState(getCurrentBSMonth());
  const [bsDay,      setBSDay]      = useState(getCurrentBSDay());
  const [selWeek,    setSelWeek]    = useState<number | null>(null);
  const [showExport, setShowExport] = useState(false);

  const { data: plantData } = useQuery({
    queryKey: ['my-plant', user?.id],
    queryFn:  () => adminApi.plant().then(r => r.data),
    staleTime: 10 * 60_000,
  });
  const plantName = (plantData as { plant?: { name?: string } } | undefined)?.plant?.name ?? `Plant ${plantId}`;

  // AD date for hourly view
  const hourlyDate = useMemo(
    () => period === 'hourly' ? fmtAD(bsToAD(bsYear, bsMonth, bsDay)) : '',
    [period, bsYear, bsMonth, bsDay],
  );

  // Date range for non-hourly views
  const { from, to } = useMemo(
    () => period === 'hourly' ? { from: '', to: '' } : getRange(period, bsYear, bsMonth, bsDay),
    [period, bsYear, bsMonth, bsDay],
  );

  // Hourly data query
  const { data: hourlyRows = [], isLoading: hourlyLoading } = useQuery({
    queryKey: ['hist-gen-hourly', hourlyDate],
    queryFn:  () => shiftLogApi.hourlyTotals({ date: hourlyDate }).then(r => r.data.data),
    enabled:  period === 'hourly' && !!hourlyDate,
  });

  // Daily-aggregated data query (all non-hourly periods)
  const { data: summaries = [], isLoading: sumLoading } = useQuery<DailyGenerationSummary[]>({
    queryKey: ['hist-gen', from, to],
    queryFn:  () => shiftLogApi.dailyTotals({ from, to }).then(r => r.data.data),
    enabled:  period !== 'hourly',
  });

  const estYears = useMemo(() => [bsYear], [bsYear]);

  const { data: estimates = [], isLoading: estLoading } = useEstimates(plantId, estYears, { enabled: period === 'hourly' });

  const isLoading = period === 'hourly' ? (hourlyLoading || estLoading) : sumLoading;

  const weeks     = useMemo(() => period === 'monthly' ? weeksInBSYear(bsYear) : [], [period, bsYear]);
  const drillWeek = selWeek != null ? weeks.find(w => w.weekNo === selWeek) : null;

  const unit = period === 'hourly' ? 'kW' : 'MWh';

  const mainData = useMemo((): GenRow[] => {
    if (period === 'hourly') {
      const planMap: Record<number, number> = {};
      estimates
        .filter(e => e.period_type === 'hour' && e.date === hourlyDate)
        .forEach(e => { if (e.hour != null) planMap[e.hour] = Number(e.estimated_kwh); });
      return hourlyRows.map(h => ({
        label:   String(h.hour).padStart(2, '0') + ':00',
        planned: planMap[h.hour] != null ? +planMap[h.hour].toFixed(2) : null,
        actual:  +Number(h.combined_kw).toFixed(2),
      }));
    }
    if (drillWeek) {
      return summaries
        .filter(s => s.date >= drillWeek.from && s.date <= drillWeek.to)
        .map(s => ({
          label:  formatBSDayLabel(new Date(s.date + 'T00:00:00')),
          actual: +(Number(s.total_generation_kwh) / 1000).toFixed(2),
        }));
    }
    return aggregateGen(summaries, period, bsYear);
  }, [period, hourlyRows, hourlyDate, estimates, summaries, drillWeek, bsYear]);

  const total = mainData.reduce((s, r) => s + r.actual, 0);

  const handleExport = () =>
    downloadCsv(
      mainData.map(r => period === 'hourly'
        ? { Period: r.label, [`Planned (${unit})`]: r.planned ?? 0, [`Actual (${unit})`]: r.actual }
        : { Period: r.label, [`Actual (${unit})`]: r.actual }),
      `generation_${period}_${period === 'hourly' ? hourlyDate : from}.csv`,
    );

  const chartTitle = period === 'hourly'
    ? `Hourly Generation (kW) — ${hourlyDate}`
    : drillWeek
      ? `Generation — ${drillWeek.label}`
      : `Actual Generation — ${period.charAt(0).toUpperCase() + period.slice(1)} view`;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <PeriodControls
          periods={GEN_PERIODS}
          period={period} onPeriod={p => { setPeriod(p); setSelWeek(null); }}
          bsYear={bsYear} onBSYear={setBSYear}
          bsMonth={bsMonth} onBSMonth={setBSMonth}
          bsDay={bsDay} onBSDay={setBSDay}
          selWeek={selWeek} onWeek={setSelWeek}
        />
        <button onClick={() => setShowExport(true)} className="btn-primary text-sm px-4">⬇ Export Excel</button>
      </div>

      {mainData.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">{period === 'hourly' ? 'Peak Output' : 'Total Generation'}</p><p className="text-xl font-bold text-slate-900">{period === 'hourly' ? `${Math.max(...mainData.map(r => r.actual)).toFixed(1)} kW` : fmtMwh(total)}</p></div>
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">{period === 'hourly' ? 'Hours Logged' : 'Periods'}</p><p className="text-xl font-bold text-slate-900">{mainData.length}</p></div>
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">Average / {period === 'hourly' ? 'Hour' : 'Period'}</p><p className="text-xl font-bold text-slate-900">{period === 'hourly' ? `${(total / mainData.length).toFixed(1)} kW` : fmtMwh(total / mainData.length)}</p></div>
        </div>
      )}

      <GenChart data={mainData} loading={isLoading} title={chartTitle} unit={unit} />

      {mainData.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto max-h-60">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-hydro-card">
                <tr className="border-b border-hydro-border text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">{period === 'hourly' ? 'Hour' : 'Period'}</th>
                  {period === 'hourly' && <th className="px-4 py-3 text-right">Planned</th>}
                  <th className="px-4 py-3 text-right">Actual</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row, i) => (
                  <tr key={i} className="table-row">
                    <td className="px-4 py-2 text-slate-700">{row.label}</td>
                    {period === 'hourly' && (
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{(row.planned ?? 0).toFixed(2)} kW</td>
                    )}
                    <td className="px-4 py-2 text-right font-mono text-slate-900">{unit === 'MWh' ? fmtMwh(row.actual) : `${row.actual.toFixed(2)} kW`}</td>
                  </tr>
                ))}
                {period !== 'hourly' && (
                  <tr className="border-t border-hydro-border bg-slate-50 font-semibold">
                    <td className="px-4 py-2 text-slate-700">Total</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-900">{fmtMwh(total)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showExport && (
        <GenExportModal
          plantId={plantId}
          plantName={plantName}
          onClose={() => setShowExport(false)}
          initPeriod={period}
          initYear={bsYear}
          initMonth={bsMonth}
          initDay={bsDay}
          initWeek={selWeek}
        />
      )}
    </div>
  );
}

// ─── ERC Export helpers ───────────────────────────────────────────────────────

type ExportType = 'weekly' | 'monthly' | 'yearly';

/** Weekly rows restricted to a single BS month (for export). */
function buildWeeklyRowsForMonth(
  summaries: DailyGenerationSummary[],
  estimates: GenerationEstimate[],
  bsYear: number,
  bsMonthIdx: number,
): ErcRow[] {
  const { from: mFrom, to: mTo } = bsMonthRangeAD(bsYear, bsMonthIdx);
  const monthSum = summaries.filter(s => s.date >= mFrom && s.date <= mTo);

  const weekEsts = estimates.filter(e => e.period_type === 'week');
  const planMap: Record<string, number> = {};
  weekEsts.forEach(e => {
    if (e.week_no != null) {
      const k = `${e.year}-W${String(e.week_no).padStart(2, '0')}`;
      planMap[k] = (planMap[k] ?? 0) + Number(e.estimated_kwh) / 1000;
    }
  });

  const actualMap: Record<string, { label: string; actual: number; sort: string }> = {};
  monthSum.forEach(s => {
    const d = new Date(s.date + 'T00:00:00');
    if (isNaN(d.getTime())) return;
    const wk = isoWeekNum(d); const wkY = isoWeekYear(d);
    const k = `${wkY}-W${String(wk).padStart(2, '0')}`;
    if (!actualMap[k]) actualMap[k] = { label: bsWeekLabel(d), actual: 0, sort: k };
    actualMap[k].actual += Number(s.total_generation_kwh) / 1000;
  });

  // include weeks that have a plan but zero actual
  Object.keys(planMap).forEach(k => {
    if (!actualMap[k]) {
      const m = k.match(/^(\d+)-W(\d+)$/);
      if (m) {
        const monAD = isoWeekMonday(parseInt(m[1]), parseInt(m[2]));
        actualMap[k] = { label: bsWeekLabel(monAD), actual: 0, sort: k };
      }
    }
  });

  return Object.entries(actualMap).sort(([, a], [, b]) => a.sort > b.sort ? 1 : -1)
    .map(([k, v]) => ({ label: v.label, planned: +(planMap[k] ?? 0).toFixed(2), actual: +v.actual.toFixed(2) }));
}

function generateErcExcel(
  rows: ErcRow[],
  meta: { type: ExportType; periodLabel: string; plant: string; unit: EnergyUnit },
) {
  const dp = unitPrecision(meta.unit);

  // Apply unit conversion (rows arrive in MWh)
  const converted = rows.map(r => ({
    ...r,
    planned: applyEnergyUnit(r.planned, meta.unit),
    actual:  applyEnergyUnit(r.actual,  meta.unit),
  }));

  const plannedTotal  = converted.reduce((s, r) => s + r.planned, 0);
  const actualTotal   = converted.reduce((s, r) => s + r.actual,  0);
  const variance      = actualTotal - plannedTotal;
  const achievement   = plannedTotal > 0 ? +(actualTotal / plannedTotal * 100).toFixed(1) : 0;

  const typeLabel: Record<ExportType, string> = {
    weekly: 'Weekly Breakdown',
    monthly: 'Monthly Breakdown',
    yearly: 'Annual Summary',
  };

  const col = (label: string) => `${label} (${meta.unit})`;

  const sheetData: (string | number)[][] = [
    ['ERC GENERATION DECLARATION REPORT', '', '', '', '', '', ''],
    [''],
    ['Plant:', meta.plant, '', 'Period:', meta.periodLabel, '', ''],
    ['Report Type:', typeLabel[meta.type], '', 'Generated:', formatBSDateFull(new Date()), '', ''],
    ['Unit:', meta.unit, '', '', '', '', ''],
    [''],
    ['S.No.', 'Period (BS)', col('Planned Generation'), col('Actual Generation'), col('Variance'), 'Achievement (%)', 'Status'],
    ...converted.map((r, i) => {
      const v   = +(r.actual - r.planned).toFixed(dp);
      const pct = r.planned > 0 ? +(r.actual / r.planned * 100).toFixed(1) : 0;
      const status = r.planned === 0 ? 'No Target'
        : pct >= 100 ? 'On Track'
        : pct >= 80  ? 'Near Target'
        : 'Below Target';
      return [i + 1, r.label, r.planned, r.actual, v, pct, status];
    }),
    [''],
    ['', 'TOTAL', +plannedTotal.toFixed(dp), +actualTotal.toFixed(dp), +variance.toFixed(dp), achievement, ''],
  ];

  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  ws['!cols'] = [
    { wch: 6 },
    { wch: 32 },
    { wch: 26 },
    { wch: 24 },
    { wch: 20 },
    { wch: 18 },
    { wch: 14 },
  ];

  // Merge title row across all columns
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ERC Declarations');

  const safePeriod = meta.periodLabel.replace(/[^a-zA-Z0-9_-]/g, '_');
  XLSX.writeFile(wb, `ERC_Declarations_${meta.type}_${safePeriod}.xlsx`);
}

// ─── ERC Export Modal ─────────────────────────────────────────────────────────

function ErcExportModal({ plantId, plantName, onClose, initPeriod, initYear, initMonth, initWeek }: {
  plantId: number;
  plantName: string;
  onClose: () => void;
  initPeriod: GenPeriod;
  initYear: number;
  initMonth: number;
  initWeek: number | null;
}) {
  const initType: ExportType = initWeek != null ? 'weekly'
    : (initPeriod === 'weekly' || initPeriod === 'monthly' || initPeriod === 'yearly') ? initPeriod
    : 'monthly';

  const [type,     setType]     = useState<ExportType>(initType);
  const [expYear,  setExpYear]  = useState(initYear);
  const [expMonth, setExpMonth] = useState(initMonth); // 0-indexed
  const [eUnit,    setEUnit]    = useState<EnergyUnit>('MWh');
  const [loading,  setLoading]  = useState(false);

  const yearOptions = getBSYearOptions(10);

  const YEARLY_COUNT = 5;
  const currentBSYear = getCurrentBSYear();
  const yearlyRange   = Array.from({ length: YEARLY_COUNT }, (_, i) => currentBSYear - YEARLY_COUNT + 1 + i);

  async function handleExport() {
    setLoading(true);
    try {
      let rows: ErcRow[];
      let periodLabel: string;

      if (type === 'weekly') {
        const { from, to } = bsMonthRangeAD(expYear, expMonth);
        const [sumRes, estRows] = await Promise.all([
          shiftLogApi.dailyTotals({ from, to }),
          estimatesApi.list(plantId, expYear).then(r => r.data.estimates as GenerationEstimate[]),
        ]);
        rows        = buildWeeklyRowsForMonth(sumRes.data.data, estRows, expYear, expMonth);
        periodLabel = `${BS_MONTHS_FULL[expMonth]} ${expYear}`;

      } else if (type === 'monthly') {
        const { from, to } = bsYearRangeAD(expYear);
        const [sumRes, estRows] = await Promise.all([
          shiftLogApi.dailyTotals({ from, to }),
          estimatesApi.list(plantId, expYear).then(r => r.data.estimates as GenerationEstimate[]),
        ]);
        rows        = buildErcRows(sumRes.data.data, estRows, 'monthly', expYear);
        periodLabel = `BS Year ${expYear}`;

      } else {
        // yearly — fetch all years in the range in a single request
        const from = bsYearRangeAD(yearlyRange[0]).from;
        const to   = bsYearRangeAD(yearlyRange[yearlyRange.length - 1]).to;
        const [sumRes, estRes] = await Promise.all([
          shiftLogApi.dailyTotals({ from, to }),
          estimatesApi.list(plantId, yearlyRange),
        ]);
        const allEst = estRes.data.estimates as GenerationEstimate[];
        rows = yearlyRange.map(y => {
          const yearEst = allEst.find(e => e.period_type === 'year' && e.year === y);
          const planned = yearEst ? +(Number(yearEst.estimated_kwh) / 1000).toFixed(2) : 0;
          const actual  = +(sumRes.data.data
            .filter((s: DailyGenerationSummary) => {
              const nd = adStrToBSSafe(s.date);
              return nd && nd.getYear() === y;
            })
            .reduce((acc: number, s: DailyGenerationSummary) => acc + Number(s.total_generation_kwh), 0) / 1000).toFixed(2);
          return { label: String(y), planned, actual };
        });
        periodLabel = `${yearlyRange[0]}–${yearlyRange[yearlyRange.length - 1]}`;
      }

      generateErcExcel(rows, { type, periodLabel, plant: plantName, unit: eUnit });
      onClose();
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  const previewText = type === 'weekly'
    ? `Weekly breakdown for ${BS_MONTHS_FULL[expMonth]} ${expYear} — one row per week`
    : type === 'monthly'
    ? `Monthly breakdown for BS Year ${expYear} — 12 rows`
    : `All years ${yearlyRange[0]}–${yearlyRange[yearlyRange.length - 1]} — one row per year`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <div>
            <h2 className="text-base font-bold text-slate-900">Export ERC Declarations</h2>
            <p className="text-xs text-slate-500 mt-0.5">Download as Excel (.xlsx)</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 transition-colors">✕</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Export type */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Export Type</p>
            <div className="grid grid-cols-3 gap-2">
              {(['weekly', 'monthly', 'yearly'] as ExportType[]).map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`py-2.5 rounded-lg text-sm font-medium capitalize border transition-colors ${
                    type === t
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Year — hidden for yearly (auto covers last N years) */}
          {type !== 'yearly' && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">BS Year</p>
              <select value={expYear} onChange={e => setExpYear(Number(e.target.value))} className="input w-full">
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}

          {/* Month — only for weekly */}
          {type === 'weekly' && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">BS Month</p>
              <select value={expMonth} onChange={e => setExpMonth(Number(e.target.value))} className="input w-full">
                {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            </div>
          )}

          {/* Unit */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Unit</p>
            <div className="flex gap-2">
              {(['kWh', 'MWh', 'GWh'] as EnergyUnit[]).map(u => (
                <button key={u} onClick={() => setEUnit(u)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    eUnit === u ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
                  }`}>
                  {u}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-50 border border-hydro-border rounded-lg px-4 py-3">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Preview</p>
            <p className="text-sm text-slate-700">{previewText}</p>
            <p className="text-xs text-slate-400 mt-1">Columns: Period · Planned · Actual · Variance · Achievement · Status</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3 justify-end border-t border-hydro-border pt-4">
          <button onClick={onClose} className="btn-ghost text-sm px-4">Cancel</button>
          <button onClick={handleExport} disabled={loading} className="btn-primary text-sm px-5 disabled:opacity-60">
            {loading ? 'Generating…' : '⬇ Export Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ERC Declarations Tab ─────────────────────────────────────────────────────

function ErcTab() {
  const { user } = useAuthStore();
  const plantId  = user?.plant_id ?? 1;

  const [period,      setPeriod]      = useState<GenPeriod>('monthly');
  const [bsYear,      setBSYear]      = useState(getCurrentBSYear());
  const [bsMonth,     setBSMonth]     = useState(getCurrentBSMonth()); // 0-indexed
  const [bsDay,       setBSDay]       = useState(getCurrentBSDay());
  const [selWeek,     setSelWeek]     = useState<number | null>(null);
  const [showExport,  setShowExport]  = useState(false);

  const { data: plantData } = useQuery({
    queryKey: ['my-plant', user?.id],
    queryFn:  () => adminApi.plant().then(r => r.data),
    staleTime: 10 * 60_000,
  });
  const plantName = (plantData as { plant?: { name?: string } } | undefined)?.plant?.name ?? `Plant ${plantId}`;

  // AD date for hourly view
  const hourlyDate = useMemo(
    () => period === 'hourly' ? fmtAD(bsToAD(bsYear, bsMonth, bsDay)) : '',
    [period, bsYear, bsMonth, bsDay],
  );

  const { from, to } = useMemo(() => {
    if (period === 'hourly') return { from: '', to: '' };
    if (period === 'yearly') return bsYearRangeAD(bsYear);
    return getRange(period, bsYear, bsMonth, bsDay);
  }, [period, bsYear, bsMonth, bsDay]);

  const estYears = useMemo(() => [bsYear], [bsYear]);

  const { data: hourlyRows = [], isLoading: hourlyLoading } = useQuery({
    queryKey: ['hist-erc-hourly', hourlyDate],
    queryFn:  () => shiftLogApi.hourlyTotals({ date: hourlyDate }).then(r => r.data.data),
    enabled:  period === 'hourly' && !!hourlyDate,
  });

  const { data: summaries = [], isLoading: sumLoading } = useQuery<DailyGenerationSummary[]>({
    queryKey: ['hist-erc-sum', from, to],
    queryFn:  () => shiftLogApi.dailyTotals({ from, to }).then(r => r.data.data),
    enabled:  period !== 'hourly',
  });

  const { data: estimates = [], isLoading: estLoading } = useEstimates(plantId, estYears);

  const weeks    = useMemo(() => period === 'monthly' ? weeksInBSYear(bsYear) : [], [period, bsYear]);
  const drillWeek = selWeek != null ? weeks.find(w => w.weekNo === selWeek) : null;

  const mainData = useMemo(() => {
    if (period === 'hourly') {
      const planMap: Record<number, number> = {};
      estimates
        .filter(e => e.period_type === 'hour' && e.date === hourlyDate)
        .forEach(e => { if (e.hour != null) planMap[e.hour] = Number(e.estimated_kwh); });
      return hourlyRows.map(h => ({
        label:   String(h.hour).padStart(2, '0') + ':00',
        planned: +(planMap[h.hour] ?? 0).toFixed(2),
        actual:  +Number(h.combined_kw).toFixed(2),
      }));
    }
    if (drillWeek) {
      const dayEst = estimates.filter(e => e.period_type === 'day');
      const planMap: Record<string, number> = {};
      dayEst.forEach(e => { if (e.date) planMap[e.date] = Number(e.estimated_kwh) / 1000; });
      return summaries
        .filter(s => s.date >= drillWeek.from && s.date <= drillWeek.to)
        .map(s => {
          const adDate = new Date(s.date + 'T00:00:00');
          return { label: formatBSDayLabel(adDate), planned: +(planMap[s.date] ?? 0).toFixed(2), actual: +(Number(s.total_generation_kwh) / 1000).toFixed(2) };
        });
    }
    return buildErcRows(summaries, estimates, period, bsYear);
  }, [period, hourlyRows, hourlyDate, summaries, estimates, drillWeek, bsYear]);

  const loading       = period === 'hourly' ? (hourlyLoading || estLoading) : (sumLoading || estLoading);
  const plannedTotal  = mainData.reduce((s, r) => s + r.planned, 0);
  const actualTotal   = mainData.reduce((s, r) => s + r.actual, 0);
  const achievement   = plannedTotal > 0 ? ((actualTotal / plannedTotal) * 100).toFixed(1) : null;

  const isHourly = period === 'hourly';
  const fmtVal   = isHourly ? (v: number) => `${v.toFixed(1)} kW` : fmtMwh;

  const chartTitle = isHourly
    ? `ERC Declarations: Planned vs Actual — ${hourlyDate}`
    : drillWeek
      ? `ERC Declarations: Planned vs Actual — ${drillWeek.label}`
      : `ERC Declarations: Planned vs Actual — ${period.charAt(0).toUpperCase() + period.slice(1)} view`;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <PeriodControls
          periods={ERC_PERIODS}
          period={period} onPeriod={setPeriod}
          bsYear={bsYear} onBSYear={setBSYear}
          bsMonth={bsMonth} onBSMonth={setBSMonth}
          bsDay={bsDay} onBSDay={setBSDay}
          selWeek={selWeek} onWeek={setSelWeek}
        />
        <button onClick={() => setShowExport(true)} className="btn-primary text-sm px-4">⬇ Export Excel</button>
      </div>

      {mainData.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">Planned Total</p><p className="text-xl font-bold text-slate-900">{fmtVal(plannedTotal)}</p></div>
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">Actual Total</p><p className="text-xl font-bold text-brand-600">{fmtVal(actualTotal)}</p></div>
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">Variance</p><p className={`text-xl font-bold ${actualTotal >= plannedTotal ? 'text-green-600' : 'text-red-500'}`}>{(actualTotal - plannedTotal) >= 0 ? '+' : '−'}{fmtVal(Math.abs(actualTotal - plannedTotal))}</p></div>
          <div className="card py-3 px-4"><p className="text-xs text-slate-500 mb-0.5">Achievement</p><p className={`text-xl font-bold ${achievement && parseFloat(achievement) >= 100 ? 'text-green-600' : 'text-amber-500'}`}>{achievement ? `${achievement}%` : 'N/A'}</p></div>
        </div>
      )}

      <ErcChart data={mainData} loading={loading} title={chartTitle} unit={isHourly ? 'kW' : 'MWh'} />

      {mainData.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto max-h-60">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-hydro-card">
                <tr className="border-b border-hydro-border text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Period</th>
                  <th className="px-4 py-3 text-right">Planned</th>
                  <th className="px-4 py-3 text-right">Actual</th>
                  <th className="px-4 py-3 text-right">Variance</th>
                  <th className="px-4 py-3 text-right">Achievement</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row, i) => {
                  const variance = row.actual - row.planned;
                  const pct = row.planned > 0 ? ((row.actual / row.planned) * 100).toFixed(1) : null;
                  return (
                    <tr key={i} className="table-row">
                      <td className="px-4 py-2 text-slate-700">{row.label}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{fmtVal(row.planned)}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-900">{fmtVal(row.actual)}</td>
                      <td className={`px-4 py-2 text-right font-mono ${variance >= 0 ? 'text-green-600' : 'text-red-500'}`}>{variance >= 0 ? '+' : '−'}{fmtVal(Math.abs(variance))}</td>
                      <td className={`px-4 py-2 text-right font-mono ${pct && parseFloat(pct) >= 100 ? 'text-green-600' : 'text-amber-600'}`}>{pct ? `${pct}%` : '—'}</td>
                    </tr>
                  );
                })}
                <tr className="border-t border-hydro-border bg-slate-50 font-semibold">
                  <td className="px-4 py-2 text-slate-700">Total</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-500">{fmtVal(plannedTotal)}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-900">{fmtVal(actualTotal)}</td>
                  <td className={`px-4 py-2 text-right font-mono ${actualTotal >= plannedTotal ? 'text-green-600' : 'text-red-500'}`}>{actualTotal >= plannedTotal ? '+' : '−'}{fmtVal(Math.abs(actualTotal - plannedTotal))}</td>
                  <td className={`px-4 py-2 text-right font-mono ${achievement && parseFloat(achievement) >= 100 ? 'text-green-600' : 'text-amber-600'}`}>{achievement ? `${achievement}%` : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showExport && (
        <ErcExportModal
          plantId={plantId}
          plantName={plantName}
          onClose={() => setShowExport(false)}
          initPeriod={period}
          initYear={bsYear}
          initMonth={bsMonth}
          initWeek={selWeek}
        />
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Historical() {
  const [tab, setTab] = useState<'generation' | 'erc'>('generation');

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-slate-900">Historical Data</h1>

      <div className="flex gap-1 border-b border-hydro-border">
        {(['generation', 'erc'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t === 'generation' ? 'Generation' : 'ERC Declarations'}
          </button>
        ))}
      </div>

      {tab === 'generation' ? <GenerationTab /> : <ErcTab />}
    </div>
  );
}
